import { type UseQueryOptions, useQuery } from '@tanstack/react-query';
import { concat } from 'lodash';
import { useContext } from 'react';
import { AuthDataProvider } from 'src/components/authProvider';
import { AuthClassification } from 'src/hooks/auth/auth.interface';
import type { FetchReturn, IAsset, ICCAsset } from 'src/interfaces';
import { generateQuery, queryKeys, requestCCApi } from 'src/utils';

import { mapCCAssets } from './helper';
import { useGetAllAssets } from './useGetAllAssets';

export const useGetCCAssets = (
  filters: string[] = [],
  overrideQuerySetting?: UseQueryOptions<ICCAsset[]>,
) => {
  const { userRole } = useContext(AuthDataProvider);
  const isOwnerOperator = userRole === AuthClassification.OWNER_OPERATOR;
  const { data: sbAssets, isSuccess: isSbAssetsReady } = useGetAllAssets();

  return useQuery({
    queryKey: queryKeys.ccAssets.all(filters),
    queryFn: () =>
      fetchCCAssets(concat(filters, 'pageSize=0'), false, sbAssets),
    enabled: isOwnerOperator || isSbAssetsReady,
    staleTime: Infinity,
    ...overrideQuerySetting,
  });
};

export const fetchCCAssets = async <T extends boolean = false>(
  filters: string[] = [],
  isPagination?: T,
  sbAssets?: IAsset[],
): Promise<FetchReturn<T, ICCAsset[]>> => {
  const response = await requestCCApi({
    url: `/assets${generateQuery(
      ['themes'],
      concat(filters, isPagination ? 'count=true' : []),
    )}`,
  });

  const ccAssets = mapCCAssets(response, sbAssets);

  if (isPagination) {
    return {
      data: ccAssets,
      totalPages: response.totalPages ?? 0,
    } as FetchReturn<T, ICCAsset[]>;
  }

  return ccAssets as FetchReturn<T, ICCAsset[]>;
};
